import type { AvatarBase } from "../../types";

export type MapAvatarAssetSlot =
  | "base"
  | "hair"
  | "faceCover"
  | "hat"
  | "shirt"
  | "top"
  | "outerwear"
  | "pants"
  | "socks"
  | "footwear"
  | "rodSkin"
  | "reelSkin"
  | "lureCharm"
  | "profileFrame"
  | "badgeA"
  | "badgeB"
  | "badgeC"
  | "background"
  | "title";

export type MapAvatarLayerKey =
  | "base"
  | "composed"
  | "hairBack"
  | "socks"
  | "pants"
  | "footwear"
  | "shirt"
  | "top"
  | "outerwear"
  | "faceCover"
  | "rodSkin"
  | "reelSkin"
  | "lureCharm"
  | "hairFront"
  | "hat";

export type MapAvatarBadgePosition = "left" | "right" | "bottom";

export type MapAvatarAssetDescriptor = {
  cosmeticId: string;
  name: string;
  slot: MapAvatarAssetSlot;
  layer?: MapAvatarLayerKey;
  asset?: string;
  tone?: string;
  accent?: string;
  badgeAsset?: string;
  badgePosition?: MapAvatarBadgePosition;
  note?: string;
  /** Compact monochrome-ish map token used when a dedicated art asset is not needed. */
  glyph?: string;
  /** Controlled Roddo accent for the map identity halo/token. */
  accentColor?: string;
};

/**
 * Canonical neutral map-avatar base.
 *
 * Product direction: do not expose gendered base labels. Existing saved legacy
 * values are normalized in mapAvatarResolver.ts and all point at this neutral
 * scaffold until hair/style customization is ready.
 */
export const MAP_AVATAR_USER_ICON_BASE = "/assets/avatars/map/bases/user_icon_v2.png";

export const MAP_AVATAR_BASE_ASSETS: Record<AvatarBase, string> = {
  user_icon: MAP_AVATAR_USER_ICON_BASE,

  // Legacy aliases kept only so old saved profiles do not break.
  boy: MAP_AVATAR_USER_ICON_BASE,
  girl: MAP_AVATAR_USER_ICON_BASE,
  dad: MAP_AVATAR_USER_ICON_BASE,
  mom: MAP_AVATAR_USER_ICON_BASE,
  grandpa: MAP_AVATAR_USER_ICON_BASE,
  grandma: MAP_AVATAR_USER_ICON_BASE,
};

// Deprecated V4.5: baked composed avatars hide equip/unequip changes.
// Keep this export for compatibility, but the resolver now renders base + equipped layers.
export const MAP_AVATAR_COMPOSED_ASSETS = {
  starter_user_icon: "/assets/avatars/map/composed/starter_user_icon.svg",
} as const;

export type MapAvatarComposedKey = keyof typeof MAP_AVATAR_COMPOSED_ASSETS;

export const MAP_AVATAR_ITEM_ASSETS: Record<string, MapAvatarAssetDescriptor> = {
  rookie_cap: {
    cosmeticId: "rookie_cap",
    name: "Rookie Cap",
    slot: "hat",
    layer: "hat",
    asset: "/assets/avatars/map/hats/rookie_cap.svg",
    glyph: "CAP",
    accentColor: "#5E8ED6",
  },
  bay_hoodie: {
    cosmeticId: "bay_hoodie",
    name: "Baleen Bay Hoodie",
    slot: "top",
    layer: "outerwear",
    asset: "/assets/avatars/map/tops/bay_hoodie.svg",
    glyph: "BB",
    accentColor: "#446FB3",
  },
  starter_rod_skin: {
    cosmeticId: "starter_rod_skin",
    name: "Starter Rod Skin",
    slot: "rodSkin",
    layer: "rodSkin",
    asset: "/assets/avatars/map/rods/starter_rod_skin.svg",
    glyph: "ROD",
    accentColor: "#5D7E99",
  },
  lucky_minnow_charm: {
    cosmeticId: "lucky_minnow_charm",
    name: "Lucky Minnow Charm",
    slot: "lureCharm",
    layer: "lureCharm",
    asset: "/assets/avatars/map/charms/lucky_minnow_charm.svg",
    glyph: "FISH",
    accentColor: "#4FAF9B",
  },
  harbor_bronze_frame: {
    cosmeticId: "harbor_bronze_frame",
    name: "Harbor Bronze Frame",
    slot: "profileFrame",
    tone: "harbor-bronze",
    glyph: "HB",
    accentColor: "#BF8650",
    accent: "/assets/avatars/map/badges/anchor.svg",
    note: "Rendered through CSS bronze ring styling around the profile bubble.",
  },
  sunset_surf_rod: {
    cosmeticId: "sunset_surf_rod",
    name: "Sunset Surf Rod",
    slot: "rodSkin",
    layer: "rodSkin",
    asset: "/assets/avatars/map/rods/sunset_surf_rod.svg",
    glyph: "SURF",
    accentColor: "#D98A62",
  },
  crew_ready_tee: {
    cosmeticId: "crew_ready_tee", name: "Crew Ready Tee", slot: "top", glyph: "CR", accentColor: "#4F7FC2",
  },
  deckhand_beanie: {
    cosmeticId: "deckhand_beanie", name: "Deckhand Beanie", slot: "hat", glyph: "DB", accentColor: "#6E7892",
  },
  dockside_cargos: {
    cosmeticId: "dockside_cargos", name: "Dockside Cargos", slot: "pants", glyph: "DC", accentColor: "#667A67",
  },
  river_boots: {
    cosmeticId: "river_boots", name: "River Boots", slot: "footwear", glyph: "RB", accentColor: "#7D684F",
  },
  harbor_windbreaker: {
    cosmeticId: "harbor_windbreaker", name: "Harbor Windbreaker", slot: "outerwear", glyph: "HW", accentColor: "#477FA2",
  },
  cobalt_reel: {
    cosmeticId: "cobalt_reel", name: "Cobalt Reel", slot: "reelSkin", glyph: "CR", accentColor: "#3D73B8",
  },
  marsh_scout_title: {
    cosmeticId: "marsh_scout_title", name: "Marsh Scout", slot: "title", glyph: "MS", accentColor: "#5F8B68",
  },
  tidewatch_cap: {
    cosmeticId: "tidewatch_cap", name: "Tidewatch Cap", slot: "hat", glyph: "TW", accentColor: "#397B9D",
  },
  tidebreaker_top: {
    cosmeticId: "tidebreaker_top", name: "Tidebreaker Top", slot: "top", glyph: "TB", accentColor: "#3987AA",
  },
  sonar_glow_charm: {
    cosmeticId: "sonar_glow_charm", name: "Sonar Glow Charm", slot: "lureCharm", glyph: "SG", accentColor: "#65D4DD",
  },
  deepwater_boots: {
    cosmeticId: "deepwater_boots", name: "Deepwater Boots", slot: "footwear", glyph: "DW", accentColor: "#4E5C73",
  },
  stormline_shell: {
    cosmeticId: "stormline_shell", name: "Stormline Shell", slot: "outerwear", glyph: "SL", accentColor: "#64749A",
  },
  moonwake_reel: {
    cosmeticId: "moonwake_reel", name: "Moonwake Reel", slot: "reelSkin", glyph: "MW", accentColor: "#7768B7",
  },
  big_blue_background: {
    cosmeticId: "big_blue_background", name: "Big Blue Background", slot: "background", glyph: "BB", tone: "big-blue", accentColor: "#3D8FC3",
  },
  moonlight_frame: {
    cosmeticId: "moonlight_frame", name: "Moonlight Frame", slot: "profileFrame", glyph: "ML", tone: "moonlight", accentColor: "#9278EC",
  },
  night_current_rod: {
    cosmeticId: "night_current_rod", name: "Night Current Rod", slot: "rodSkin", glyph: "NC", accentColor: "#5E63A8",
  },
  dockmaster_title: {
    cosmeticId: "dockmaster_title", name: "Dockmaster", slot: "title", glyph: "DM", accentColor: "#B78C48",
  },
  founding_tide_frame: {
    cosmeticId: "founding_tide_frame", name: "Founding Tide Frame", slot: "profileFrame", glyph: "F5", tone: "founding-tide", accentColor: "#55B6C8",
  },
  founding_minnow_charm: {
    cosmeticId: "founding_minnow_charm", name: "Founding Minnow Charm", slot: "lureCharm", glyph: "F5", accentColor: "#55B6C8",
  },
  first_cast_founder_title: {
    cosmeticId: "first_cast_founder_title", name: "First Cast Founder", slot: "title", glyph: "FC", accentColor: "#D1A653",
  },

  golden_bass_frame: {
    cosmeticId: "golden_bass_frame",
    name: "Golden Bass Frame",
    slot: "profileFrame",
    tone: "golden-bass",
    glyph: "WIN",
    accentColor: "#E0B540",
    accent: "/assets/avatars/map/badges/trophy.svg",
    note: "Rendered through CSS gold ring styling around the profile bubble.",
  },
};

const pickBySlot = <TSlot extends MapAvatarAssetSlot>(slot: TSlot) =>
  Object.fromEntries(
    Object.entries(MAP_AVATAR_ITEM_ASSETS).filter(([, item]) => item.slot === slot),
  ) as Record<string, MapAvatarAssetDescriptor>;

// Backwards-compatible exports. Keep these until all callers use MAP_AVATAR_ITEM_ASSETS directly.
export const MAP_AVATAR_HAT_ITEMS = pickBySlot("hat");
export const MAP_AVATAR_TOP_ITEMS = pickBySlot("top");
export const MAP_AVATAR_ROD_ITEMS = pickBySlot("rodSkin");
export const MAP_AVATAR_CHARM_ITEMS = pickBySlot("lureCharm");
export const MAP_AVATAR_PROFILE_FRAME_ITEMS = pickBySlot("profileFrame");

export const MAP_AVATAR_FALLBACKS = {
  base: "user_icon" as AvatarBase,
  profileFrameTone: "default" as const,

  hat: "/assets/avatars/map/hats/rookie_cap.svg",
  hair: "/assets/avatars/map/hair/default.svg",
  faceCover: "/assets/avatars/map/face-covers/placeholder.svg",
  top: "/assets/avatars/map/tops/bay_hoodie.svg",
  outerwear: "/assets/avatars/map/outerwear/placeholder_jacket.svg",
  pants: "/assets/avatars/map/bottoms/placeholder_pants.svg",
  socks: "/assets/avatars/map/socks/placeholder_socks.svg",
  footwear: "/assets/avatars/map/footwear/placeholder_boots.svg",
  rodSkin: "/assets/avatars/map/rods/starter_rod_skin.svg",
  reelSkin: "/assets/avatars/map/reels/placeholder_reel.svg",
  lureCharm: "/assets/avatars/map/charms/lucky_minnow_charm.svg",
  badge: "/assets/avatars/map/badges/shell.svg",
};